import React, { useState, useRef, useCallback } from 'react';
import { Upload, X, FileText, Loader2, Mail } from 'lucide-react';
import type { ModuleType } from '../../types/demo';
import { getModuleConfig } from '../../types/demo';
import {
  submitDemo,
  validateFile,
  formatFileSize
} from '../../services/demoService';
import { isRateLimitResponse, isSuccessResponse } from '../../types/demo';
import type { UserInfo } from './DemoSection';

interface DemoFormProps {
  moduleType: ModuleType;
  onSuccess: (email: string) => void;
  onRateLimited: (retryAfter: string, message: string) => void;
  initialUserInfo?: UserInfo;
  onUserInfoChange?: (info: UserInfo) => void;
}

export function DemoForm({
  moduleType,
  onSuccess,
  onRateLimited,
  initialUserInfo,
  onUserInfoChange
}: DemoFormProps) {
  const [firstName, setFirstName] = useState(initialUserInfo?.firstName ?? '');
  const [lastName, setLastName] = useState(initialUserInfo?.lastName ?? '');
  const [email, setEmail] = useState(initialUserInfo?.email ?? '');
  const [company, setCompany] = useState(initialUserInfo?.company ?? '');
  const [files, setFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ firstName?: string; lastName?: string; email?: string; company?: string; file?: string }>({});

  const fileInputRef = useRef<HTMLInputElement>(null);
  const moduleConfig = getModuleConfig(moduleType);
  const isMultipleFiles = moduleConfig.multipleFiles ?? false;
  const maxFiles = moduleConfig.maxFiles ?? 20;

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleFileSelect = useCallback((selectedFiles: File[]) => {
    // Validate each file against module-specific accepted types
    for (const f of selectedFiles) {
      const validation = validateFile(f, moduleConfig.acceptedFileTypes);
      if (!validation.valid) {
        setFieldErrors(prev => ({ ...prev, file: validation.error }));
        return;
      }
    }

    if (isMultipleFiles) {
      // Add to existing files, respecting max limit
      setFiles(prev => {
        const combined = [...prev, ...selectedFiles];
        if (combined.length > maxFiles) {
          setFieldErrors(prev => ({ ...prev, file: `Maximum ${maxFiles} files allowed` }));
          return prev;
        }
        return combined;
      });
    } else {
      // Single file mode - replace
      setFiles(selectedFiles.slice(0, 1));
    }
    setFieldErrors(prev => ({ ...prev, file: undefined }));
    setError(null);
  }, [isMultipleFiles, maxFiles, moduleConfig.acceptedFileTypes]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      handleFileSelect(droppedFiles);
    }
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length > 0) {
      handleFileSelect(selectedFiles);
    }
    // Reset input so the same file can be selected again if removed
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const removeAllFiles = () => {
    setFiles([]);
  };

  const validateForm = (): boolean => {
    const errors: { firstName?: string; lastName?: string; email?: string; company?: string; file?: string } = {};

    if (!firstName.trim()) {
      errors.firstName = 'First name is required';
    } else if (firstName.trim().length > 50) {
      errors.firstName = 'First name must be 50 characters or less';
    }

    if (!lastName.trim()) {
      errors.lastName = 'Last name is required';
    } else if (lastName.trim().length > 50) {
      errors.lastName = 'Last name must be 50 characters or less';
    }

    if (!email.trim()) {
      errors.email = 'Email is required';
    } else if (!validateEmail(email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (company.trim().length > 100) {
      errors.company = 'Company must be 100 characters or less';
    }

    if (files.length === 0) {
      errors.file = isMultipleFiles
        ? 'Please select at least one file to analyze'
        : 'Please select a file to analyze';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateForm() || files.length === 0) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Minimum 1.5s delay so user sees the spinner
      const [response] = await Promise.all([
        submitDemo({
          moduleType,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim(),
          company: company.trim(),
          ...(isMultipleFiles ? { files } : { file: files[0] })
        }),
        new Promise(resolve => setTimeout(resolve, 1500))
      ]);

      if (isSuccessResponse(response)) {
        onUserInfoChange?.({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim(),
          company: company.trim()
        });
        onSuccess(email.trim());
      } else if (isRateLimitResponse(response)) {
        onRateLimited(response.retryAfter, response.message);
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 sm:p-8 shadow-sm">
      {/* Header */}
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-gsp-text-primary">
          {moduleConfig.title}
        </h3>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* First Name and Last Name - Side by Side */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="demo-firstName" className="block text-sm font-medium text-gsp-text-primary mb-1.5">
              First Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="demo-firstName"
              value={firstName}
              onChange={(e) => {
                setFirstName(e.target.value);
                setFieldErrors(prev => ({ ...prev, firstName: undefined }));
              }}
              className={`
                w-full px-4 py-3 rounded-lg border bg-white text-gsp-text-primary
                focus:outline-none focus:ring-2 focus:ring-gsp-teal/20 focus:border-gsp-teal
                transition-colors
                ${fieldErrors.firstName ? 'border-red-400' : 'border-gray-200'}
              `}
              placeholder="First name"
              disabled={isSubmitting}
            />
            {fieldErrors.firstName && (
              <p className="mt-1 text-sm text-red-500">{fieldErrors.firstName}</p>
            )}
          </div>
          <div>
            <label htmlFor="demo-lastName" className="block text-sm font-medium text-gsp-text-primary mb-1.5">
              Last Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="demo-lastName"
              value={lastName}
              onChange={(e) => {
                setLastName(e.target.value);
                setFieldErrors(prev => ({ ...prev, lastName: undefined }));
              }}
              className={`
                w-full px-4 py-3 rounded-lg border bg-white text-gsp-text-primary
                focus:outline-none focus:ring-2 focus:ring-gsp-teal/20 focus:border-gsp-teal
                transition-colors
                ${fieldErrors.lastName ? 'border-red-400' : 'border-gray-200'}
              `}
              placeholder="Last name"
              disabled={isSubmitting}
            />
            {fieldErrors.lastName && (
              <p className="mt-1 text-sm text-red-500">{fieldErrors.lastName}</p>
            )}
          </div>
        </div>

        {/* Email and Company - Side by Side */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Email Field */}
          <div>
            <label htmlFor="demo-email" className="block text-sm font-medium text-gsp-text-primary mb-1.5">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="demo-email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setFieldErrors(prev => ({ ...prev, email: undefined }));
              }}
              className={`
                w-full px-4 py-3 rounded-lg border bg-white text-gsp-text-primary
                focus:outline-none focus:ring-2 focus:ring-gsp-teal/20 focus:border-gsp-teal
                transition-colors
                ${fieldErrors.email ? 'border-red-400' : 'border-gray-200'}
              `}
              placeholder="you@company.com"
              disabled={isSubmitting}
            />
            {fieldErrors.email && (
              <p className="mt-1 text-sm text-red-500">{fieldErrors.email}</p>
            )}
          </div>

          {/* Company Field */}
          <div>
            <label htmlFor="demo-company" className="block text-sm font-medium text-gsp-text-primary mb-1.5">
              Company
            </label>
            <input
              type="text"
              id="demo-company"
              value={company}
              onChange={(e) => {
                setCompany(e.target.value);
                setFieldErrors(prev => ({ ...prev, company: undefined }));
              }}
              className={`
                w-full px-4 py-3 rounded-lg border bg-white text-gsp-text-primary
                focus:outline-none focus:ring-2 focus:ring-gsp-teal/20 focus:border-gsp-teal
                transition-colors
                ${fieldErrors.company ? 'border-red-400' : 'border-gray-200'}
              `}
              placeholder="Your company"
              disabled={isSubmitting}
            />
            {fieldErrors.company && (
              <p className="mt-1 text-sm text-red-500">{fieldErrors.company}</p>
            )}
          </div>
        </div>

        {/* File Upload */}
        <div>
          <label className="block text-sm font-medium text-gsp-text-primary mb-1.5">
            {isMultipleFiles ? 'Documents' : 'Document'} <span className="text-red-500">*</span>
            {isMultipleFiles && files.length > 0 && (
              <span className="ml-2 text-gsp-text-body font-normal">
                ({files.length} file{files.length !== 1 ? 's' : ''} selected)
              </span>
            )}
          </label>

          {/* Drop zone - always show if multiple files allowed or no files selected */}
          {(isMultipleFiles || files.length === 0) && (
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
              className={`
                relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer
                transition-all duration-200
                ${isDragging
                  ? 'border-gsp-teal bg-gsp-teal/5'
                  : fieldErrors.file
                    ? 'border-red-400 bg-red-50'
                    : 'border-gray-300 hover:border-gsp-teal hover:bg-gray-50'
                }
              `}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={moduleConfig.acceptedFileTypes.join(',')}
                onChange={handleFileInputChange}
                className="hidden"
                disabled={isSubmitting}
                multiple={isMultipleFiles}
              />
              <Upload className={`w-10 h-10 mx-auto mb-3 ${isDragging ? 'text-gsp-teal' : 'text-gray-400'}`} />
              <p className="text-sm text-gsp-text-body mb-1">
                <span className="font-medium text-gsp-teal">Click to upload</span> or drag and drop
              </p>
              <p className="text-xs text-gray-500">
                {isMultipleFiles
                  ? `PDF files up to 50MB each (max ${maxFiles} files)`
                  : 'PDF or Excel files up to 50MB'
                }
              </p>
              {isMultipleFiles && (
                <p className="text-xs text-gsp-teal mt-2">
                  Upload your lease and any amendments, assignments, or guaranties
                </p>
              )}
            </div>
          )}

          {/* Single file display (non-multiple mode) */}
          {!isMultipleFiles && files.length > 0 && (
            <div className="flex items-center gap-3 p-4 bg-gsp-teal/5 rounded-lg border border-gsp-teal/20">
              <div className="w-10 h-10 rounded-lg bg-gsp-teal/20 flex items-center justify-center">
                <FileText className="w-5 h-5 text-gsp-teal" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gsp-text-primary truncate">
                  {files[0].name}
                </p>
                <p className="text-xs text-gsp-text-body">
                  {formatFileSize(files[0].size)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => removeFile(0)}
                disabled={isSubmitting}
                className="p-1 text-gray-400 hover:text-red-500 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          )}

          {/* Multiple files list */}
          {isMultipleFiles && files.length > 0 && (
            <div className="mt-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gsp-text-body">
                  {files.reduce((sum, f) => sum + f.size, 0) > 0 && (
                    <>Total: {formatFileSize(files.reduce((sum, f) => sum + f.size, 0))}</>
                  )}
                </span>
                <button
                  type="button"
                  onClick={removeAllFiles}
                  disabled={isSubmitting}
                  className="text-xs text-red-500 hover:text-red-700 transition-colors"
                >
                  Remove all
                </button>
              </div>
              <div className="max-h-48 overflow-y-auto space-y-2 pr-1">
                {files.map((f, index) => (
                  <div
                    key={`${f.name}-${index}`}
                    className="flex items-center gap-3 p-3 bg-gsp-teal/5 rounded-lg border border-gsp-teal/20"
                  >
                    <div className="w-8 h-8 rounded-lg bg-gsp-teal/20 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-4 h-4 text-gsp-teal" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gsp-text-primary truncate">
                        {f.name}
                      </p>
                      <p className="text-xs text-gsp-text-body">
                        {formatFileSize(f.size)}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      disabled={isSubmitting}
                      className="p-1 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {fieldErrors.file && (
            <p className="mt-1 text-sm text-red-500">{fieldErrors.file}</p>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting}
          className={`
            w-full flex items-center justify-center gap-2 px-6 py-4 rounded-lg
            font-semibold text-white transition-all duration-200
            ${isSubmitting
              ? 'bg-gsp-orange/70 cursor-not-allowed'
              : 'bg-gsp-orange hover:bg-gsp-orange/90 shadow-lg hover:shadow-xl'
            }
          `}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Processing...
            </>
          ) : (
            'Submit for analysis'
          )}
        </button>

        {/* Email Delivery Note */}
        <p className="flex items-center justify-center gap-2 text-sm text-gsp-text-body">
          <Mail className="w-4 h-4" />
          Results will be emailed to you shortly
        </p>
      </form>
    </div>
  );
}

export default DemoForm;
